import React from "react";
import styles from "./LinkCard.module.css";
import { addAnalyticsParam } from "../../utils/analytics";

interface LinkCardProps {
  // The Tire Rack destination. analytics param is appended here so every caller
  // gets consistent tracking on the outbound link.
  url: string;
  // Small uppercase descriptor of what the link is (e.g. "Tire search",
  // "Tire Rack page").
  eyebrow: string;
  // The prominent label (the page/search name). Ignored when `body` is supplied.
  label?: string;
  // Custom body in place of `label` (e.g. the tire-browse staggered Front/Rear rows).
  body?: React.ReactNode;
  // CTA button text (e.g. "Browse tires →", "Visit page →").
  ctaText: string;
  // data-track-id on the card — the tracking identity ("tire_browse_card" |
  // "navigation_card" | …). Keeps each card's analytics distinct even though they
  // share one visual.
  trackId: string;
  trackOption?: string;
  trackTurn?: number;
  // Extra card class (e.g. the staggered layout tweak).
  className?: string;
  // Extra data-* attributes on the card (e.g. data-staggered).
  cardData?: Record<string, string>;
}

/**
 * LinkCard — the single visual for a "go to this Tire Rack page" card: a small
 * eyebrow + a prominent label on the left, a CTA button on the right. Standardizes
 * the format across every wayfinding/destination card (tire_browse_card via
 * TireBrowseCard, navigation_card) so they read identically. Callers supply the
 * copy + tracking identity; the layout lives here.
 */
const LinkCard: React.FC<LinkCardProps> = ({
  url,
  eyebrow,
  label,
  body,
  ctaText,
  trackId,
  trackOption,
  trackTurn,
  className,
  cardData,
}) => (
  <div
    className={`${styles.card} ${className || ""}`.trim()}
    data-track-id={trackId}
    data-track-turn={trackTurn}
    {...cardData}
  >
    <div className={styles.header}>
      <div className={styles.eyebrow}>{eyebrow}</div>
      {body ?? <div className={styles.size}>{label}</div>}
    </div>
    <a
      href={addAnalyticsParam(url)}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.cta}
      data-track-option={trackOption}
    >
      {ctaText}
    </a>
  </div>
);

export default LinkCard;
export { styles as linkCardStyles };
